import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { getAuthRedirectBaseUrl } from "@/lib/authRedirect";
import { sendSystemNotification } from "@/lib/notificationEmail";
import { buildLoginMessage, buildLogoutMessage } from "@/lib/notificationMessages";
import { logOperationalError, logOperationalWarning } from "@/lib/operationalLogger";
import {
  normalizeEmail,
  normalizeUserRole,
  resolveMasterAccess,
} from "@/lib/masterAccess";
import type { User, Session } from "@supabase/supabase-js";
import { canEnableTestLogin } from "@/hooks/useTestLoginGuard";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isMaster: boolean;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  profile: { full_name: string; phone: string | null } | null;
  professional: { id: string; nickname: string | null; name: string; user_type: string } | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInAsTestUser: () => void;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

type ProfileRow = {
  full_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  phone?: string | null;
  role?: string | null;
};

type SupabaseErrorLike = {
  code?: string;
  message?: string;
};

type ProfessionalLike = {
  id: string | null;
  user_id?: string | null;
  full_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  email?: string | null;
  role?: string | null;
  user_type?: string | null;
};

type ProfessionalQueryResult = {
  data: ProfessionalLike | null;
  error: SupabaseErrorLike | null;
};

type ProfileWithLegacyColumns = Database["public"]["Tables"]["profiles"]["Row"] & {
  name?: string | null;
};

type AuthSnapshot = {
  isMaster: boolean;
  profile: { full_name: string; phone: string | null } | null;
  professional: { id: string; nickname: string | null; name: string; user_type: string } | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TEST_LOGIN_KEY = "nitfisio-test-login";
const TEST_LOGIN_FEATURE_ENABLED = canEnableTestLogin(
  import.meta.env.DEV === true,
  import.meta.env.VITE_ENABLE_TEST_LOGIN,
);
const TEST_USER = {
  id: "00000000-0000-0000-0000-000000000001",
  email: "qa-test-user@nitfisio.local",
  fullName: "QA Test User",
  nickname: "qa",
};

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const AUTH_PROFILE_SYNC_TIMEOUT_MS = 8_000;
const AUTH_SNAPSHOT_TIMEOUT_MS = 12_000;
const AUTH_INITIAL_SESSION_TIMEOUT_MS = 10_000;
const AUTH_SNAPSHOT_CACHE_KEY = "serafisio:auth-snapshot-v1";

type CachedAuthSnapshot = AuthSnapshot & { userId: string; cachedAt: number };

const readCachedAuthSnapshot = (userId: string): AuthSnapshot | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_SNAPSHOT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedAuthSnapshot;
    if (parsed.userId !== userId) return null;
    return { isMaster: parsed.isMaster, profile: parsed.profile, professional: parsed.professional };
  } catch {
    return null;
  }
};

const writeCachedAuthSnapshot = (userId: string, snapshot: AuthSnapshot) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      AUTH_SNAPSHOT_CACHE_KEY,
      JSON.stringify({ ...snapshot, userId, cachedAt: Date.now() }),
    );
  } catch {
    // Ignore quota errors
  }
};

const clearCachedAuthSnapshot = () => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(AUTH_SNAPSHOT_CACHE_KEY);
  } catch {
    // Ignore
  }
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(timeoutMessage));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMaster, setIsMaster] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [profile, setProfile] = useState<{ full_name: string; phone: string | null } | null>(null);
  const [professional, setProfessional] = useState<{ id: string; nickname: string | null; name: string; user_type: string } | null>(null);
  const onlinePresenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const syncedProfileUserRef = useRef<string | null>(null);
  const syncingProfileRef = useRef(false);
  const authPipelineTokenRef = useRef(0);
  const lastUserSnapshotRef = useRef<Map<string, AuthSnapshot>>(new Map());
  const activateTestUserRef = useRef<() => void>(() => undefined);
  const processAuthSessionRef = useRef<(event: string, nextSession: Session | null) => Promise<void>>(async () => undefined);

  const teardownOnlinePresenceChannel = () => {
    if (!onlinePresenceChannelRef.current) return;
    supabase.removeChannel(onlinePresenceChannelRef.current);
    onlinePresenceChannelRef.current = null;
  };

  const startOnlinePresenceChannel = (userId: string) => {
    teardownOnlinePresenceChannel();

    const connect = () => {
      // Re-check that we still want this channel (user may have signed out
      // before the idle callback fired).
      if (onlinePresenceChannelRef.current) return;

      const channel = supabase.channel("app-online-users", {
        config: { presence: { key: userId } },
      });

      channel.subscribe(async (status) => {
        if (status !== "SUBSCRIBED") return;
        await channel.track({ userId, onlineAt: new Date().toISOString() });
      });

      onlinePresenceChannelRef.current = channel;
    };

    // PERF: Opening a Realtime WebSocket during boot competes with the
    // critical render path. Defer until the browser is idle.
    if (typeof window === "undefined") {
      connect();
      return;
    }
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(connect, { timeout: 3000 });
    } else {
      window.setTimeout(connect, 1500);
    }
  };

  const activateTestUser = () => {
    if (!TEST_LOGIN_FEATURE_ENABLED) {
      localStorage.removeItem(TEST_LOGIN_KEY);
      return;
    }

    startOnlinePresenceChannel(TEST_USER.id);
    setUser({
      id: TEST_USER.id,
      email: TEST_USER.email,
      app_metadata: {},
      user_metadata: { full_name: TEST_USER.fullName },
      aud: "authenticated",
      created_at: new Date().toISOString(),
    } as User);
    setSession(null);
    setIsMaster(false);
    setProfile({ full_name: TEST_USER.fullName, phone: null });
    setProfessional({ id: TEST_USER.id, name: TEST_USER.fullName, nickname: TEST_USER.nickname, user_type: "user" });
    setLoading(false);
    localStorage.setItem(TEST_LOGIN_KEY, "1");
  };

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  const registerLoginTimestamp = async (userId: string) => {
    const lastLoginAt = new Date().toISOString();

    const byUserIdResponse = await supabase
      .from("profiles")
      .update({ last_login: lastLoginAt })
      .eq("user_id", userId)
      .select("id")
      .limit(1);

    const shouldFallback =
      shouldFallbackToProfileId(byUserIdResponse.error) || (byUserIdResponse.data?.length ?? 0) === 0;

    if (!shouldFallback) return;

    const byProfileIdResponse = await supabase
      .from("profiles")
      .update({ last_login: lastLoginAt })
      .eq("id", userId)
      .select("id")
      .limit(1);

    if (byProfileIdResponse.error) {
      throw byProfileIdResponse.error;
    }

    if ((byProfileIdResponse.data?.length ?? 0) === 0) {
      throw new Error("Nenhum perfil encontrado para registrar login");
    }
  };

  const handleLoginNotification = async (authenticatedUser: User) => {
    const profileName = await resolveProfileDisplayName(authenticatedUser.id);
    const fallbackName =
      authenticatedUser.user_metadata?.nickname ||
      authenticatedUser.user_metadata?.full_name ||
      authenticatedUser.email ||
      "Usuário";

    // Wait briefly for the Supabase client session to fully propagate
    // before invoking edge functions that require an auth token.
    setTimeout(() => {
      sendSystemNotification({
        notification_type: "login",
        ...buildLoginMessage({
          userName: profileName || fallbackName,
        }),
        actor_user_id: authenticatedUser.id,
      });
    }, 1500);
  };

  const handleLogoutNotification = async () => {
    const profileName = user?.id ? await resolveProfileDisplayName(user.id) : null;
    await sendSystemNotification({
      notification_type: "logout",
      ...buildLogoutMessage({
        userName:
          profileName ||
          professional?.nickname ||
          profile?.full_name ||
          user?.user_metadata?.nickname ||
          user?.user_metadata?.full_name ||
          user?.email ||
          "Usuário",
      }),
      actor_user_id: user?.id,
    });
  };

  const resolveProfileDisplayName = async (userId: string): Promise<string | null> => {
    const byUserIdResponse = await supabase
      .from("profiles")
      .select("nickname, full_name, name, email")
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle();

    const shouldFallback =
      shouldFallbackToProfileId(byUserIdResponse.error as SupabaseErrorLike | null) ||
      (!byUserIdResponse.data && UUID_V4ISH_REGEX.test(userId));

    const finalResponse = shouldFallback
      ? await supabase
          .from("profiles")
          .select("nickname, full_name, name, email")
          .eq("id", userId)
          .limit(1)
          .maybeSingle()
      : byUserIdResponse;

    const data = finalResponse.data as {
      nickname?: string | null;
      full_name?: string | null;
      name?: string | null;
      email?: string | null;
    } | null;

    return data?.nickname?.trim() || data?.full_name?.trim() || data?.name?.trim() || data?.email?.trim() || null;
  };

  const shouldFallbackToProfileId = (error: SupabaseErrorLike | null) => {
    if (!error) return false;
    if (error.code === "42703") return true;

    const normalizedMessage = error.message?.toLowerCase() ?? "";
    return normalizedMessage.includes("user_id") && normalizedMessage.includes("does not exist");
  };

  const isMissingColumnError = (error: SupabaseErrorLike | null, columnName: string) => {
    if (!error) return false;

    const normalizedMessage = error.message?.toLowerCase() ?? "";
    return normalizedMessage.includes(columnName.toLowerCase()) && normalizedMessage.includes("does not exist");
  };

  const fetchProfessionalById = async (table: "v_professionals", userId: string): Promise<ProfessionalQueryResult> => {
    const canonicalResult = await supabase
      .from(table)
      .select("id, full_name, nickname, email, role, user_type")
      .eq("id", userId)
      .maybeSingle();

    if (!isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "full_name") && !isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "role")) {
      return canonicalResult;
    }

    return supabase
      .from(table)
      .select("id, name, nickname, email, user_type")
      .eq("id", userId)
      .maybeSingle();
  };

  const fetchProfessionalByEmail = async (table: "v_professionals", email: string): Promise<ProfessionalQueryResult> => {
    const canonicalResult = await supabase
      .from(table)
      .select("id, full_name, nickname, email, role, user_type")
      .eq("email", email)
      .maybeSingle();

    if (!isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "full_name") && !isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "role")) {
      return canonicalResult;
    }

    return supabase
      .from(table)
      .select("id, name, nickname, email, user_type")
      .eq("email", email)
      .maybeSingle();
  };

  const PROFILE_COLUMNS = "id, user_id, full_name, name, nickname, phone, role, email, last_login, blocked";

  const fetchProfileByUser = async (userId: string) => {
    const profileByUserId = await supabase.from("profiles").select(PROFILE_COLUMNS).eq("user_id", userId).maybeSingle();

    if (!shouldFallbackToProfileId(profileByUserId.error)) return profileByUserId;

    return supabase.from("profiles").select(PROFILE_COLUMNS).eq("id", userId).maybeSingle();
  };

  const syncAuthenticatedProfile = async (authUser?: User | null) => {
    try {
      const user = authUser ?? (await supabase.auth.getUser()).data.user;
      if (!user) return;

      if (syncingProfileRef.current) return;
      if (syncedProfileUserRef.current === user.id) return;

      if (!UUID_V4ISH_REGEX.test(user.id)) {
        logOperationalError("[auth] invalid_uuid_for_profile_sync", new Error("Invalid UUID for profile sync"), { userId: user.id });
        return;
      }

      syncingProfileRef.current = true;

      const existingProfessionalByUserRes = await fetchProfessionalById("v_professionals", user.id);
      const normalizedUserEmail = normalizeEmail(user.email);

      const [existingProfileByUserRes, existingProfileByIdRes, existingProfessionalByEmailRes] = await Promise.all([
        supabase.from("profiles").select("role, full_name, name, email").eq("user_id", user.id).maybeSingle(),
        supabase.from("profiles").select("role").eq("id", user.id).maybeSingle(),
        user.email
          ? fetchProfessionalByEmail("v_professionals", normalizedUserEmail)
          : Promise.resolve({ data: null, error: null }),
      ]);

      const hasMasterAccess = resolveMasterAccess({
        profileRole: existingProfileByUserRes.data?.role,
        fallbackProfileRole: existingProfileByIdRes.data?.role,
        professionalType: existingProfessionalByUserRes.data?.user_type,
        professionalRole: existingProfessionalByEmailRes.data?.role,
        email: user.email,
      });
      const currentProfileRole = existingProfileByUserRes.data?.role ?? existingProfileByIdRes.data?.role;
      const effectiveRole = hasMasterAccess
        ? "master"
        : (currentProfileRole && currentProfileRole !== "user" ? currentProfileRole : "professional");

      const existingFullName = existingProfileByUserRes.data?.full_name || existingProfileByUserRes.data?.name;
      const metadataName = user.user_metadata?.name ?? user.user_metadata?.full_name ?? "";
      const existingProfileRole = existingProfileByUserRes.data?.role;
      const existingProfileEmail = normalizeEmail((existingProfileByUserRes.data as { email?: string | null } | null)?.email);
      const normalizedUserEmailForProfile = normalizeEmail(user.email);

      const shouldSkipUpsert =
        !!existingProfileByUserRes.data
        && existingProfileRole === effectiveRole
        && existingProfileEmail === normalizedUserEmailForProfile
        && !!existingFullName;

      if (shouldSkipUpsert) {
        syncedProfileUserRef.current = user.id;
        return;
      }

      const payload: {
        user_id: string;
        email: string;
        role: string;
        updated_at: string;
        name?: string;
      } = {
        user_id: user.id,
        email: user.email ?? "",
        role: effectiveRole,
        updated_at: new Date().toISOString(),
      };

      // Only set name if profile doesn't already have a full_name
      if (!existingFullName) {
        payload.name = metadataName;
      }

      

      const response = await supabase.from("profiles").upsert(payload, { onConflict: "user_id" });

      if (response.error) {
        logOperationalError("[auth] profile_upsert_failed", response.error, { userId: user.id });
        return;
      }

      syncedProfileUserRef.current = user.id;
      
    } catch (error) {
      logOperationalError("[auth] unexpected_error_while_syncing_profile", error);
    } finally {
      syncingProfileRef.current = false;
    }
  };

  const fetchUserData = async (userId: string, userEmail?: string | null): Promise<AuthSnapshot> => {
    const normalizedEmail = normalizeEmail(userEmail) || null;

    const [profileRes, professionalByUserIdRes, professionalByEmailRes] = await Promise.all([
      fetchProfileByUser(userId),
      fetchProfessionalById("v_professionals", userId),
      normalizedEmail
        ? fetchProfessionalByEmail("v_professionals", normalizedEmail)
        : Promise.resolve({ data: null, error: null }),
    ]);

    const professionalData: ProfessionalLike | null =
      professionalByUserIdRes.data
      ?? professionalByEmailRes.data
      ?? null;

    const normalizedProfessionalType = normalizeUserRole(
      professionalData?.user_type ?? professionalData?.role,
    );
    const profileRow = profileRes.data as ProfileWithLegacyColumns | null;

    return {
      isMaster: resolveMasterAccess({
        professionalType: professionalData?.user_type,
        professionalRole: professionalData?.role,
        profileRole: (profileRow as ProfileRow | null)?.role,
        metadataRole: (user?.user_metadata as { role?: string } | undefined)?.role,
        email: userEmail,
      }),
      profile: profileRow
        ? {
            full_name: profileRow.full_name ?? profileRow.name ?? "",
            phone: profileRow.phone ?? null,
          }
        : null,
      professional:
        professionalData?.id
          ? {
              id: professionalData.id,
              nickname: professionalData.nickname ?? null,
              name:
                professionalData.full_name?.trim()
                || professionalData.name?.trim()
                || profileRow?.full_name?.trim()
                || normalizedEmail?.split("@")[0]
                || "Profissional",
              user_type: normalizedProfessionalType,
            }
          : null,
    };
  };

  const applyAuthSnapshot = (snapshot: AuthSnapshot) => {
    setIsMaster(snapshot.isMaster);
    setProfile(snapshot.profile);
    setProfessional(snapshot.professional);
  };

  const processAuthSession = async (event: string, nextSession: Session | null) => {
    const pipelineToken = ++authPipelineTokenRef.current;

    if (!nextSession?.user) {
      syncedProfileUserRef.current = null;
      clearCachedAuthSnapshot();
      teardownOnlinePresenceChannel();
      setSession(null);
      setUser(null);
      setIsMaster(false);
      setProfile(null);
      setProfessional(null);
      setLoading(false);
      return;
    }

    const nextUser = nextSession.user;
    setSession(nextSession);
    setUser(nextUser);

    const cachedSnapshot =
      lastUserSnapshotRef.current.get(nextUser.id) ?? readCachedAuthSnapshot(nextUser.id);
    if (cachedSnapshot) {
      lastUserSnapshotRef.current.set(nextUser.id, cachedSnapshot);
      applyAuthSnapshot(cachedSnapshot);
      setLoading(false);
    } else {
      setLoading(true);
    }

    startOnlinePresenceChannel(nextUser.id);

    if (event === "SIGNED_IN") {
      try {
        await withTimeout(
          syncAuthenticatedProfile(nextUser),
          AUTH_PROFILE_SYNC_TIMEOUT_MS,
          "Auth profile sync timeout",
        );
      } catch (error) {
        logOperationalWarning("[auth] profile_sync_timeout_or_failure", {
          userId: nextUser.id,
          event,
          reason: error instanceof Error ? error.message : String(error),
        });
      }
    }

    const refreshSnapshot = async () => {
      try {
        const snapshot = await withTimeout(
          fetchUserData(nextUser.id, nextUser.email),
          AUTH_SNAPSHOT_TIMEOUT_MS,
          "Auth snapshot fetch timeout",
        );
        if (pipelineToken !== authPipelineTokenRef.current) return;

        lastUserSnapshotRef.current.set(nextUser.id, snapshot);
        writeCachedAuthSnapshot(nextUser.id, snapshot);
        applyAuthSnapshot(snapshot);
      } catch (error) {
        logOperationalError("[auth] failed_to_fetch_user_data_snapshot", error, {
          userId: nextUser.id,
          event,
        });
        if (pipelineToken !== authPipelineTokenRef.current) return;
        if (!cachedSnapshot) {
          applyAuthSnapshot({ isMaster: false, profile: null, professional: null });
        }
      } finally {
        setLoading(false);
      }
    };

    // PERF: When we already have a cached snapshot, the UI is interactive.
    // Defer the network refresh to idle so it doesn't compete with first paint.
    if (cachedSnapshot && typeof window !== "undefined") {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(() => void refreshSnapshot(), { timeout: 4000 });
      } else {
        window.setTimeout(() => void refreshSnapshot(), 1500);
      }
    } else {
      await refreshSnapshot();
    }
  };

  // Ref-pattern (latest-ref): atualizamos as refs durante o render, sem useEffect.
  // Justificativa:
  //  - `activateTestUser` e `processAuthSession` são fechados sobre estado/setters do componente
  //    e seriam recriados a cada render. Envolvê-los em useCallback exigiria propagar deps por
  //    todo o pipeline de auth (alto risco de regressão em uma área sensível).
  //  - Atribuir refs em fase de render é seguro porque é idempotente e não dispara efeitos.
  //  - Os useEffects abaixo permanecem mount-only e sempre invocam a versão mais recente via ref.
  activateTestUserRef.current = activateTestUser;
  processAuthSessionRef.current = processAuthSession;

  useEffect(() => {
    if (TEST_LOGIN_FEATURE_ENABLED && localStorage.getItem(TEST_LOGIN_KEY) === "1") {
      activateTestUserRef.current();
      return;
    }

    if (!TEST_LOGIN_FEATURE_ENABLED) {
      localStorage.removeItem(TEST_LOGIN_KEY);
    }

    let subscriptionRef: { unsubscribe: () => void } | null = null;

    const initializeAuth = async () => {
      try {
        // 1. Restore session from storage FIRST to avoid stale refresh-token errors
        const { data: { session: restoredSession }, error: sessionError } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_INITIAL_SESSION_TIMEOUT_MS,
          "Auth session initialization timeout",
        );

        if (sessionError) {
          logOperationalWarning("[auth] stale_session_detected_signing_out", { reason: sessionError.message });
          await supabase.auth.signOut();
          await processAuthSessionRef.current("INITIAL_SESSION", null);
        } else {
          await processAuthSessionRef.current("INITIAL_SESSION", restoredSession);
        }
      } catch (error) {
        logOperationalError("[auth] failed_to_initialize_auth_session", error);
        setSession(null);
        setUser(null);
        setIsMaster(false);
        setProfile(null);
        setProfessional(null);
      } finally {
        setLoading(false);
      }

      // 2. AFTER session is restored, subscribe to subsequent auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        // Skip the INITIAL_SESSION event — we already handled it above
        if (event === "INITIAL_SESSION") return;

        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
        }
        void processAuthSessionRef.current(event, session);
      });
      subscriptionRef = subscription;
    };

    initializeAuth();

    return () => {
      teardownOnlinePresenceChannel();
      subscriptionRef?.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    const credentialsToTry = [password];
    if (trimmedPassword && trimmedPassword !== password) {
      credentialsToTry.push(trimmedPassword);
    }

    let error: Error | null = null;
    let data: { user: User | null } | null = null;

    for (const candidatePassword of credentialsToTry) {
      const signInResult = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: candidatePassword,
      });

      error = signInResult.error as Error | null;
      data = signInResult.data;

      if (!error) {
        break;
      }
    }

    // Record login timestamp and notification
    const authenticatedUser = data?.user;
    if (!error && authenticatedUser) {
      // Check if user is blocked
      const { data: profileData } = await supabase
        .from("profiles")
        .select("blocked")
        .or(`user_id.eq.${authenticatedUser.id},id.eq.${authenticatedUser.id}`)
        .limit(1)
        .maybeSingle();

      if (profileData?.blocked) {
        await supabase.auth.signOut();
        return { error: new Error("Seu acesso foi bloqueado. Entre em contato com o administrador.") };
      }

      // Ensure profile exists BEFORE trying to register login timestamp
      await syncAuthenticatedProfile(authenticatedUser);
      registerLoginTimestamp(authenticatedUser.id).catch((loginTimestampError) => {
        logOperationalError("[auth] failed_to_register_login_timestamp", loginTimestampError, { userId: authenticatedUser.id });
      });
      handleLoginNotification(authenticatedUser).catch((loginNotificationError) => {
        logOperationalError("[auth] failed_to_register_login_notification", loginNotificationError, { userId: authenticatedUser.id });
      });
    }
    return { error };
  };

  const signInAsTestUser = () => {
    if (!TEST_LOGIN_FEATURE_ENABLED) return;

    activateTestUser();
    registerLoginTimestamp(TEST_USER.id).catch((loginTimestampError) => {
      logOperationalError("[auth] failed_to_register_test_login_timestamp", loginTimestampError, { userId: TEST_USER.id });
    });
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const { error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: getAuthRedirectBaseUrl(),
        data: {
          full_name: fullName,
          role: "user",
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Send notification BEFORE signing out so the session is still valid
    try {
      await handleLogoutNotification();
    } catch (logoutNotificationError) {
      logOperationalError("[auth] failed_to_register_logout_notification", logoutNotificationError, { userId: user?.id });
    }
    localStorage.removeItem(TEST_LOGIN_KEY);
    clearCachedAuthSnapshot();
    teardownOnlinePresenceChannel();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsMaster(false);
    setProfile(null);
    setProfessional(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isMaster, isPasswordRecovery, clearPasswordRecovery, profile, professional, signIn, signInAsTestUser, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// Justificativa: `useAuth` coexiste com `AuthProvider` neste arquivo de propósito.
// Separar exigiria atualizar 20+ importadores em todo o app por um trade-off apenas de DX
// (Fast Refresh do hook). O Provider raramente sofre HMR isolado e a alteração não compensa
// o risco/ruído de churn cross-cutting.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
